"use client"

/**
 * UnifiedPriceDisplay - Consistent price display for products
 *
 * Handles both rental and purchase prices with consistent formatting.
 * Use this component everywhere instead of custom price formatting.
 */

import { cn } from "@/lib/utils"
import { convertToLocale } from "@lib/util/money"
import { getUnitLabel } from "@lib/util/rental"
import type { RentalInfo, RentalUnit } from "@/types/rental"
import { useLocale, useTranslations } from "next-intl"

/**
 * Price formatting options
 */
export interface PriceFormatOptions {
  /** Currency code (e.g., 'CZK', 'USD') */
  currencyCode: string
  /** Locale for formatting (e.g., 'cs-CZ', 'en-US') */
  locale?: string
}

/**
 * Format price for display
 * @param amount - Price in major units (e.g., 100 for 100 CZK)
 * @param options - Formatting options
 */
export function formatDisplayPrice(
  amount: number,
  options: PriceFormatOptions
): string {
  return convertToLocale({
    amount,
    currency_code: options.currencyCode,
    locale: options.locale || 'en-US',
  })
}

/**
 * Format rental price with unit
 * @param amount - Price per period in major units
 * @param unit - Rental unit (day, week, month)
 * @param options - Formatting options
 */
export function formatRentalPrice(
  amount: number,
  unit: RentalUnit,
  options: PriceFormatOptions & { appLocale?: string }
): { price: string; unitLabel: string } {
  const price = formatDisplayPrice(amount, options)
  const unitLabel = getUnitLabel(unit, 1, options.appLocale || 'en')
  return { price, unitLabel }
}

/**
 * Props for UnifiedPriceDisplay component
 */
export interface UnifiedPriceDisplayProps {
  /** Rental info from API */
  rentalInfo?: RentalInfo | null
  /** Purchase price (already formatted string, e.g., "500 Kč") */
  purchasePrice?: string | null
  /** Purchase price as number (in minor units/cents) */
  purchasePriceNumber?: number | null
  /** Currency code */
  currencyCode: string
  /** Display variant */
  variant?: 'default' | 'compact' | 'card'
  /** Show purchase label when both prices shown */
  showPurchaseLabel?: boolean
  /** Custom class name */
  className?: string
}

/**
 * Unified price display component
 *
 * Shows rental and/or purchase prices based on availability mode:
 * - sale_only: only purchase price
 * - rental_only: only rental price
 * - both: rental price (primary) + purchase price (secondary)
 */
export function UnifiedPriceDisplay({
  rentalInfo,
  purchasePrice,
  purchasePriceNumber,
  currencyCode,
  variant = 'default',
  showPurchaseLabel = true,
  className,
}: UnifiedPriceDisplayProps) {
  const locale = useLocale()
  const t = useTranslations('product.rental')

  // Build intl locale
  const localeMap: Record<string, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    cs: 'cs-CZ',
    uk: 'uk-UA',
  }
  const intlLocale = localeMap[locale] || 'en-US'

  // Determine what to show
  const showRentalPrice = rentalInfo?.isRentalAvailable &&
    rentalInfo.rentalPrice != null &&
    rentalInfo.rentalUnit

  const showPurchasePrice = !rentalInfo ||
    !rentalInfo.isRentalAvailable ||
    rentalInfo.availabilityMode === 'sale_only' ||
    rentalInfo.availabilityMode === 'both'

  // Format rental price
  const rentalPriceFormatted = showRentalPrice
    ? formatRentalPrice(rentalInfo.rentalPrice!, rentalInfo.rentalUnit!, {
        currencyCode: rentalInfo.currencyCode || currencyCode,
        appLocale: locale,
      })
    : null

  // Format purchase price if needed
  const purchasePriceFormatted = purchasePrice || (
    purchasePriceNumber != null
      ? formatDisplayPrice(purchasePriceNumber / 100, { currencyCode, locale: intlLocale })
      : null
  )

  // Variant-specific styles
  const styles = {
    default: {
      container: "flex flex-col gap-1",
      rentalPrice: "text-xl font-semibold text-primary",
      rentalUnit: "text-sm text-foreground-secondary",
      purchaseLabel: "text-sm",
      purchasePrice: showRentalPrice ? "text-base text-foreground-secondary" : "text-xl font-semibold",
    },
    compact: {
      container: "flex flex-col gap-0.5",
      rentalPrice: "text-base font-medium text-primary",
      rentalUnit: "text-xs text-foreground-secondary",
      purchaseLabel: "text-xs",
      purchasePrice: showRentalPrice ? "text-sm text-foreground-secondary" : "text-base font-medium",
    },
    card: {
      container: "space-y-1",
      rentalPrice: "text-lg font-semibold text-primary",
      rentalUnit: "text-sm font-normal text-foreground-secondary ml-1",
      purchaseLabel: "mr-1",
      purchasePrice: showRentalPrice ? "text-sm text-foreground-secondary" : "text-lg font-semibold text-primary",
    },
  }

  const s = styles[variant]

  if (!showRentalPrice && !showPurchasePrice) {
    return null
  }

  return (
    <div className={cn(s.container, className)}>
      {/* Rental price (primary) */}
      {showRentalPrice && rentalPriceFormatted && (
        <div className="flex items-baseline gap-1">
          <span className={s.rentalPrice} data-testid="rental-price">
            {rentalPriceFormatted.price}
          </span>
          <span className={s.rentalUnit}>
            / {rentalPriceFormatted.unitLabel}
          </span>
        </div>
      )}

      {/* Purchase price */}
      {showPurchasePrice && purchasePriceFormatted && (
        <div className={cn("flex items-baseline", showRentalPrice && "gap-1")}>
          {showRentalPrice && showPurchaseLabel && (
            <span className={s.purchaseLabel}>{t('buyPrice')}:</span>
          )}
          <span className={s.purchasePrice} data-testid="purchase-price">
            {purchasePriceFormatted}
          </span>
        </div>
      )}
    </div>
  )
}

export default UnifiedPriceDisplay
